import React, { FunctionComponent, createContext, useCallback, useState } from "react";
import {
  DetailedRouteFragment,
  GetRouteWithIdDocument,
  useGetRouteWithIdQuery,
  useUpdateRouteLocationMutation,
} from "../../../api/nachfahrbarkeit-api/generated";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/react-hooks";

interface IRouteContextProviderProps {
  routeId: string;
}

interface IRouteContext {
  route: DetailedRouteFragment | undefined;
  routeLoading: boolean;
  updateLocation: (newLocationId: string) => void;
  selectedStopId: string | null;
  setSelectedStopId: (stopId: string | null) => void;
}

export const RouteContext = createContext<IRouteContext>({
  route: undefined,
  routeLoading: false,
  updateLocation: () => null,
  selectedStopId: null,
  setSelectedStopId: () => null,
});

export const RouteContextProvider: FunctionComponent<IRouteContextProviderProps> = (props) => {
  const { routeId, children } = props;
  const { t } = useTranslation();
  const { data: routeData, loading: routeLoading } = useGetRouteWithIdQuery({
    fetchPolicy: "cache-first",
    variables: { id: routeId },
  });
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);
  const client = useApolloClient();

  const onLocationUpdateCompleted = useCallback(
    (newLocationId: string) => {
      client.writeQuery({
        query: GetRouteWithIdDocument,
        variables: { id: routeData?.getRouteWithId.id },
        data: {
          getRouteWithId: {
            ...routeData?.getRouteWithId,
            location_id: newLocationId,
          },
        },
      });
      toast.success(t("routes_management.location_select.success"));
    },
    [routeData, client, t],
  );

  const [updateRouteLocation] = useUpdateRouteLocationMutation({
    onError: () => toast.error(t("routes_management.location_select.error")),
  });

  const updateLocation = useCallback(
    (newLocationId: string) => {
      if (!routeData) {
        return;
      }

      updateRouteLocation({
        variables: {
          routeId: routeData.getRouteWithId.id,
          locationId: newLocationId,
        },
        // onCompleted not working :(
      }).then(() => {
        onLocationUpdateCompleted(newLocationId);
      });
    },
    [routeData, updateRouteLocation, onLocationUpdateCompleted],
  );

  return (
    <RouteContext.Provider
      value={{ route: routeData?.getRouteWithId, routeLoading, updateLocation, selectedStopId, setSelectedStopId }}
    >
      {children}
    </RouteContext.Provider>
  );
};
